/**
 * Ideal projectile motion: no air resistance, constant gravity, a flat landing level.
 *
 * Launch from height h₀ above the ground at speed v₀ and angle θ above the horizontal
 * (negative θ is a throw downward). With vₓ = v₀ cos θ and v_y = v₀ sin θ:
 *
 *   x(t) = vₓ t                       y(t) = h₀ + v_y t − ½ g t²
 *
 * so the flight ends when y = 0 at t = (v_y + √(v_y² + 2 g h₀)) / g, and the range is
 * R = vₓ t. The peak is at t = v_y / g when v_y > 0, at height h₀ + v_y² / (2g)
 * (OpenStax University Physics Vol. 1, §4.3 "Projectile Motion").
 *
 * The angle that maximises range from height h₀ is atan(v₀ / √(v₀² + 2 g h₀)), which is
 * 45° only when h₀ = 0.
 */

export interface ProjectileInput {
  /** Launch speed v₀ in m/s, ≥ 0. */
  speed: number;
  /** Launch angle in radians, from −π/2 to π/2. */
  angle: number;
  /** Launch height h₀ above the landing level in metres, ≥ 0. */
  height: number;
  /** Gravitational acceleration in m/s², > 0. */
  gravity: number;
}

export type ProjectileError =
  | "negative-speed"
  | "negative-height"
  | "bad-gravity"
  | "bad-angle"
  | "no-flight";

export interface ProjectileResult {
  vx: number;
  vy0: number;
  timeOfFlight: number;
  range: number;
  /** Time at which the highest point is reached (0 when launched level or downward). */
  peakTime: number;
  /** Horizontal distance at the highest point. */
  peakX: number;
  /** Highest height above the landing level. */
  maxHeight: number;
  /** Vertical velocity at impact (negative: downward). */
  impactVy: number;
  impactSpeed: number;
  /** Angle below the horizontal at impact, in radians, 0 to π/2. */
  impactAngle: number;
  /** Angle that gives the longest range from this speed and height, in radians. */
  bestAngle: number;
  bestRange: number;
}

export interface TrajectoryPoint {
  t: number;
  x: number;
  y: number;
}

const HALF_PI = Math.PI / 2;
const ANGLE_SLACK = 1e-12;

export function solveProjectile(
  input: ProjectileInput
): ProjectileResult | { error: ProjectileError } {
  const { speed, angle, height, gravity } = input;
  if (speed < 0) {
    return { error: "negative-speed" };
  }
  if (height < 0) {
    return { error: "negative-height" };
  }
  if (!(gravity > 0)) {
    return { error: "bad-gravity" };
  }
  if (Math.abs(angle) > HALF_PI + ANGLE_SLACK) {
    return { error: "bad-angle" };
  }
  const vx = speed * Math.cos(angle);
  const vy0 = speed * Math.sin(angle);
  const root = Math.sqrt(vy0 * vy0 + 2 * gravity * height);
  const timeOfFlight = (vy0 + root) / gravity;
  if (!(timeOfFlight > 0)) {
    return { error: "no-flight" };
  }
  // At exactly ±90° cos is 6e-17, not 0; treat it as a vertical throw.
  const horizontal = Math.abs(vx) < 1e-12 * Math.max(1, speed) ? 0 : vx;
  const peakTime = vy0 > 0 ? vy0 / gravity : 0;
  const impactVy = vy0 - gravity * timeOfFlight;
  const bestRange =
    (speed / gravity) * Math.sqrt(speed * speed + 2 * gravity * height);
  return {
    vx: horizontal,
    vy0,
    timeOfFlight,
    range: horizontal * timeOfFlight,
    peakTime,
    peakX: horizontal * peakTime,
    maxHeight: height + (vy0 > 0 ? (vy0 * vy0) / (2 * gravity) : 0),
    impactVy,
    impactSpeed: Math.hypot(horizontal, impactVy),
    impactAngle: Math.atan2(-impactVy, Math.abs(horizontal)),
    bestAngle: Math.atan2(
      speed,
      Math.sqrt(speed * speed + 2 * gravity * height)
    ),
    bestRange,
  };
}

/** Position at time t (clamped to the flight). */
export function positionAt(
  input: ProjectileInput,
  result: ProjectileResult,
  t: number
): TrajectoryPoint {
  const time = Math.min(Math.max(t, 0), result.timeOfFlight);
  return {
    t: time,
    x: result.vx * time,
    y: Math.max(
      0,
      input.height + result.vy0 * time - 0.5 * input.gravity * time * time
    ),
  };
}

/** `count + 1` points evenly spaced in time from launch to landing. */
export function trajectory(
  input: ProjectileInput,
  result: ProjectileResult,
  count = 80
): TrajectoryPoint[] {
  const points: TrajectoryPoint[] = [];
  for (let i = 0; i <= count; i += 1) {
    points.push(positionAt(input, result, (result.timeOfFlight * i) / count));
  }
  return points;
}

/** Surface gravity of the bodies offered as presets, m/s². */
export const GRAVITY_PRESETS = {
  earth: 9.806_65,
  moon: 1.62,
  mars: 3.73,
} as const;
